import React, { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useT } from "@/i18n";
import { MOTIVO_REQUIRED, transitionLabelKey } from "@/lib/projects";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";

/** Cambio de estado de la obra. Pausar y cancelar exigen motivo y quedan en auditoría. */
export const ProjectTransitionDialog = ({ projectId, origen, destino, onOpenChange, onDone }) => {
  const { t } = useT();
  const qc = useQueryClient();
  const [motivo, setMotivo] = useState("");
  const open = Boolean(destino);
  const needsMotivo = MOTIVO_REQUIRED.includes(destino);

  useEffect(() => { if (open) setMotivo(""); }, [open]);

  const mutation = useMutation({
    mutationFn: () => api.post(`/projects/${projectId}/transition`, { estado: destino, motivo: motivo || undefined }),
    onSuccess: () => {
      toast.success(t("app.saved"));
      qc.invalidateQueries({ queryKey: ["project-detail", projectId] });
      qc.invalidateQueries({ queryKey: ["projects"] });
      onDone?.();
      onOpenChange(false);
    },
    onError: (e) => toast.error(apiError(e, t("app.error"))),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="project-transition-dialog" className="max-w-[440px] rounded-[10px]">
        <DialogHeader>
          <DialogTitle>{destino && t(`projects.transition.${transitionLabelKey(origen, destino)}`)}</DialogTitle>
          <DialogDescription>{destino && t(`projects.status.${destino}`)}</DialogDescription>
        </DialogHeader>

        {needsMotivo && (
          <div>
            <Label className="text-[12.5px]">
              {destino === "pausado" ? t("projects.transition.pause_reason") : t("projects.transition.cancel_reason")} *
            </Label>
            <Textarea data-testid="project-transition-reason-input" rows={3} value={motivo}
              onChange={(e) => setMotivo(e.target.value)} placeholder={t("projects.transition.reason_ph")} />
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="h-9">{t("app.cancel")}</Button>
          <Button data-testid="project-transition-confirm" className="h-9"
            disabled={mutation.isPending || (needsMotivo && motivo.trim().length < 5)}
            onClick={() => mutation.mutate()}>
            {mutation.isPending && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
            {t("projects.transition.confirm")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
